import React from "react";
import {
  Box,
  Button,
  Hidden,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { LinkToShare } from "./apps-types";
import { useTranslation } from "react-i18next";
import LinkProgress from "./LinkProgress";
import NoDataToBeShown from "../reusable/NoDataToBeShown";
import compareTime from "../../utils/compareTime";
import { grey } from "@mui/material/colors";
import LinkSummaryDialog from "./LinkGenerationSummary";
import HttpsIcon from "@mui/icons-material/Https";
import NoEncryptionGmailerrorredIcon from "@mui/icons-material/NoEncryptionGmailerrorred";
import ShareIcon from "@mui/icons-material/Share";

export default function LinkDetailsTable(props: {
  name: string;
  columns: string[];
  links: LinkToShare[];
  isLoading: boolean;
  openDialog: boolean;
  setOpenDialog: any;
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [uuid, setuuid] = React.useState<LinkToShare>();
  const { t } = useTranslation();

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSetDialogInfo = (element: LinkToShare) => {
    props.setOpenDialog(true);
    setuuid(element);
  };

  const skeletonArray = Array(5).fill("");

  const baseUrl = window.location.protocol + "//" + window.location.host;

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table sx={{ width: "100%" }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell key={props.columns[0]} align="center">
                {props.columns[0]}
              </TableCell>
              <Hidden smDown>
                <TableCell key={props.columns[1]} align="center">
                  {props.columns[1]}
                </TableCell>
              </Hidden>
              <TableCell key={props.columns[2]} align="center">
                {props.columns[2]}
              </TableCell>
              <TableCell key={"auth"} align="center">
                {t("Authentication")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.isLoading ? (
              skeletonArray.map((item, index) => (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row" align="center">
                    <Skeleton />
                  </TableCell>
                  <TableCell component="th" scope="row" align="center">
                    <Skeleton />
                  </TableCell>
                  <Hidden mdDown>
                    <TableCell align="center">
                      {" "}
                      <Skeleton />
                    </TableCell>
                    <TableCell align="center">
                      {" "}
                      <Skeleton />
                    </TableCell>
                  </Hidden>
                </TableRow>
              ))
            ) : props.links.length > 0 ? (
              props.links
                .sort((a, b) =>
                  new Date(a.validity_end).getTime() <
                  new Date(b.validity_end).getTime()
                    ? 1
                    : -1
                )
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((element: LinkToShare, index) => (
                  <TableRow
                    key={index}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell
                      key={"version-" + element.id}
                      component="th"
                      scope="row"
                      align="center"
                    >
                      {element.versions[1]
                        ? element.versions[0].versionNumber +
                          ", " +
                          element.versions[1]?.versionNumber
                        : element.versions[0].versionNumber}
                    </TableCell>
                    <Hidden smDown>
                      <TableCell align="center">
                        {t("Expiring at: ") +
                          new Date(element.validity_end).toLocaleString()}
                      </TableCell>
                    </Hidden>
                    <TableCell
                      key={"progress-" + element.id}
                      component="th"
                      scope="row"
                      align="center"
                    >
                      {compareTime(
                        element.validity_end,
                        element.validity_start
                      ) === "valid" ? (
                        <Box>
                          <Button onClick={() => handleSetDialogInfo(element)}>
                            {t("See details")}
                          </Button>
                        </Box>
                      ) : (
                        <Box display="flex" justifyContent="center">
                          <LinkProgress
                            validity_end_date={element.validity_end}
                            validity_start_date={element.validity_start}
                          />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell component="th" scope="row" align="center">
                      {element.isAuthenticated ? (
                        <HttpsIcon
                          color="action"
                          sx={{ width: 25, height: 25 }}
                        />
                      ) : (
                        <NoEncryptionGmailerrorredIcon
                          color="action"
                          sx={{ width: 25, height: 25 }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={4}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      height: "30vh",
                      alignItems: "center",
                    }}
                  >
                    <Stack justifyContent="center" alignItems="center">
                      <ShareIcon sx={{ fontSize: 40, color: grey[500] }} />
                      <NoDataToBeShown />
                      <Typography textAlign="center">
                        {t(
                          "Generate a link to start sharing this application."
                        )}
                      </Typography>
                    </Stack>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        labelRowsPerPage={t("Rows per page")}
        rowsPerPageOptions={[5, 10, 25, 100]}
        component="div"
        count={props.links.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {uuid && (
        <LinkSummaryDialog
          open={props.openDialog}
          setOpen={props.setOpenDialog}
          generatedLinkText={
            uuid.isAuthenticated
              ? (
                  baseUrl + `/${props.name}/download/auth/${uuid.uuid}`
                ).replaceAll(" ", "%20")
              : (baseUrl + `/${props.name}/download/${uuid.uuid}`).replaceAll(
                  " ",
                  "%20"
                )
          }
          generatedLink={uuid}
          name={props.name}
        />
      )}
    </Box>
  );
}
